/**
 * Thin fetch wrappers that handle CSRF tokens and JSON headers.
 *
 * These are for background JSON calls (barcode lookups, AI analysis,
 * item saves, etc.) — NOT for Inertia page navigation. Inertia pages
 * use `router.visit()` / `router.post()` from @inertiajs/vue3.
 *
 * Rails includes a CSRF meta tag in the layout; these helpers read it
 * once per call and set the appropriate headers so callers don't need
 * to repeat the boilerplate.
 */

export function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
}

/** Shared implementation for POST/PATCH/PUT/DELETE with optional JSON body + CSRF. */
function mutationJson(
  method: string,
  url: string,
  body?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-CSRF-Token": csrfToken(),
  }
  if (body) headers["Content-Type"] = "application/json"

  return fetch(url, {
    method,
    signal,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
}

/**
 * GET JSON from a Rails endpoint.
 *
 * @param url - The endpoint URL (with query params already appended).
 * @param signal - Optional AbortSignal for cancellation.
 * @returns The raw Response (caller handles status checks).
 */
export function getJson(url: string, signal?: AbortSignal): Promise<Response> {
  return fetch(url, {
    signal,
    headers: { Accept: "application/json" },
  })
}

/** POST JSON to a Rails endpoint with CSRF token. */
export function postJson(
  url: string,
  body?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Response> {
  return mutationJson("POST", url, body, signal)
}

/** POST multipart form data without overriding the browser-generated content boundary. */
export function postFormData(url: string, body: FormData, signal?: AbortSignal): Promise<Response> {
  return fetch(url, {
    method: "POST",
    signal,
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body,
  })
}

/** PATCH JSON to a Rails endpoint with CSRF token. */
export function patchJson(
  url: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Response> {
  return mutationJson("PATCH", url, body, signal)
}

/** DELETE to a Rails endpoint with CSRF token. */
export function deleteJson(url: string, signal?: AbortSignal): Promise<Response> {
  return mutationJson("DELETE", url, undefined, signal)
}
